/**
 * ExportSettings — Masterlisten-Export (Admin Einstellungen → Export).
 *
 * 1:1-Portierung des Import/Export-Bereichs aus CRM 1.0
 * (`sh24-frontend/src/screens/settings/ImportSettings.js`), fachlich identisch:
 *
 *  - Jahr wählen (10 Jahre zurück), Schalter "nur aktive BKs/PBs".
 *  - "Exportieren" reiht serverseitig denselben Queue-Job ein wie CRM 1.0
 *    (ExportCaresJob → Care_Export), es entsteht also exakt dieselbe Datei.
 *  - Die Dateiliste wird alle 4 Sekunden gepollt (wie in CRM 1.0), bis der
 *    Status von "processing" auf "finished" oder "failed" springt.
 *  - Download holt die Datei als Blob (Bearer-Token nötig).
 *
 * Endpoints: /v2/admin/exports (siehe exportsApi in lib/exports.ts).
 */
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Card, SectionTitle, Button, Field, Badge } from '../../components/ui';
import { Icon } from '../../components/icons';
import { getThemeColors, BRAND } from '../../lib/theme';
import { useIsMobile } from '../../lib/useBreakpoint';
import { formatDateTime } from '../../lib/dateFormat';
import { exportsApi, type ExportFile } from '../../lib/exports';
import type { ThemeColors } from '../../types';

interface ExportSettingsProps {
  dark: boolean;
}

/** Poll-Intervall der Dateiliste — identisch zu CRM 1.0 (useInterval 4s). */
const POLL_MS = 4000;

/** Auswahl: aktuelles Jahr und die 9 Jahre davor (wie CRM 1.0). */
const YEARS: number[] = Array.from({ length: 10 }, (_, i) => new Date().getFullYear() - i);

function selectStyle(t: ThemeColors): React.CSSProperties {
  return {
    width: '100%', border: `1px solid ${t.border}`, borderRadius: 9,
    padding: '9px 12px', fontSize: 13, color: t.heading, background: t.inputBg,
    fontFamily: 'Outfit, sans-serif', cursor: 'pointer', outline: 'none',
  };
}

export function ExportSettings({ dark }: ExportSettingsProps) {
  const t = getThemeColors(dark);
  const { t: tr } = useTranslation();
  const isMobile = useIsMobile();

  const [year, setYear] = useState<number>(YEARS[0]);
  const [onlyActive, setOnlyActive] = useState(true);

  const [files, setFiles] = useState<ExportFile[]>([]);
  const [listError, setListError] = useState(false);
  const [loading, setLoading] = useState(true);

  const [starting, setStarting] = useState(false);
  const [startError, setStartError] = useState<string | null>(null);
  const [started, setStarted] = useState(false);

  const [downloadingId, setDownloadingId] = useState<number | null>(null);
  const [downloadError, setDownloadError] = useState<string | null>(null);

  // Verhindert, dass eine langsame Antwort eine neuere überschreibt.
  const inFlight = useRef(false);

  const loadFiles = useCallback(async () => {
    if (inFlight.current) return;
    inFlight.current = true;
    try {
      setFiles(await exportsApi.list());
      setListError(false);
    } catch {
      setListError(true);
    } finally {
      inFlight.current = false;
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void loadFiles();
    const id = window.setInterval(() => void loadFiles(), POLL_MS);
    return () => window.clearInterval(id);
  }, [loadFiles]);

  const startExport = async () => {
    setStarting(true);
    setStartError(null);
    setStarted(false);
    try {
      await exportsApi.startCares(year, onlyActive);
      setStarted(true);
      await loadFiles();
    } catch {
      setStartError(tr('settings.export.start_error'));
    } finally {
      setStarting(false);
    }
  };

  const download = async (file: ExportFile) => {
    setDownloadingId(file.id);
    setDownloadError(null);
    try {
      await exportsApi.download(file);
    } catch {
      setDownloadError(tr('settings.export.download_error'));
    } finally {
      setDownloadingId(null);
    }
  };

  return (
    <div style={{ display: 'grid', gridTemplateColumns: isMobile ? '1fr' : '1fr 1fr', gap: 16, alignItems: 'start' }}>

      {/* ── Export anstoßen ─────────────────────────────────────────────── */}
      <Card dark={dark} style={{ padding: 20 }}>
        <SectionTitle
          dark={dark}
          title={tr('settings.export.title')}
          subtitle={tr('settings.export.subtitle')}
        />

        <Field label={tr('settings.export.year')}>
          <select
            aria-label={tr('settings.export.year')}
            value={year}
            onChange={e => setYear(Number(e.target.value))}
            style={selectStyle(t)}
          >
            {YEARS.map(y => <option key={y} value={y}>{y}</option>)}
          </select>
        </Field>

        <label style={{
          display: 'flex', alignItems: 'flex-start', gap: 9, marginTop: 14,
          fontSize: 13, color: t.text, cursor: 'pointer', lineHeight: 1.45,
        }}>
          <input
            type="checkbox"
            checked={onlyActive}
            onChange={e => setOnlyActive(e.target.checked)}
            style={{ marginTop: 2, accentColor: BRAND, cursor: 'pointer' }}
          />
          <span>{tr('settings.export.only_active', { from: year - 1, to: year })}</span>
        </label>

        <div style={{ marginTop: 8, fontSize: 12, color: t.muted }}>
          {onlyActive
            ? tr('settings.export.hint_with_previous_year')
            : tr('settings.export.hint_selected_year_only')}
        </div>

        <div style={{ marginTop: 18, display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap' }}>
          <Button variant="primary" dark={dark} disabled={starting} onClick={() => void startExport()}>
            {starting ? tr('settings.export.starting') : tr('settings.export.start')}
          </Button>
          {started && !startError && (
            <span style={{ fontSize: 12, color: '#027a48' }}>{tr('settings.export.started')}</span>
          )}
          {startError && <span style={{ fontSize: 12, color: '#b42318' }}>{startError}</span>}
        </div>
      </Card>

      {/* ── Erzeugte Dateien ────────────────────────────────────────────── */}
      <Card dark={dark} style={{ padding: 20 }}>
        <SectionTitle
          dark={dark}
          title={tr('settings.export.files_title')}
          subtitle={tr('settings.export.files_subtitle')}
        />

        {loading && <div style={{ fontSize: 13, color: t.muted }}>{tr('settings.export.loading')}</div>}

        {!loading && listError && (
          <div style={{ fontSize: 13, color: '#b42318' }}>{tr('settings.export.list_error')}</div>
        )}

        {!loading && !listError && files.length === 0 && (
          <div style={{ fontSize: 13, color: t.muted }}>{tr('settings.export.empty')}</div>
        )}

        {!loading && !listError && files.length > 0 && (
          <div style={{ display: 'flex', flexDirection: 'column' }}>
            {files.map(file => (
              <div
                key={file.id}
                style={{
                  display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                  gap: 12, padding: '10px 0', borderBottom: `1px solid ${t.border}`,
                }}
              >
                <div style={{ minWidth: 0 }}>
                  <div style={{
                    fontSize: 13, color: t.heading, fontWeight: 500,
                    overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
                  }}>
                    {file.file}
                  </div>
                  {file.created_at && (
                    <div style={{ fontSize: 11, color: t.muted, marginTop: 2 }}>
                      {formatDateTime(file.created_at)}
                    </div>
                  )}
                </div>

                <div style={{ flexShrink: 0 }}>
                  {file.status === 'processing' && (
                    <Badge tone="warning" dark={dark}>{tr('settings.export.status.processing')}</Badge>
                  )}
                  {file.status === 'failed' && (
                    <Badge tone="error" dark={dark}>{tr('settings.export.status.failed')}</Badge>
                  )}
                  {file.status === 'finished' && (
                    <Button
                      variant="secondary"
                      size="sm"
                      dark={dark}
                      disabled={downloadingId === file.id}
                      onClick={() => void download(file)}
                    >
                      <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
                        <Icon.Download c={dark ? '#d0d5dd' : '#667085'} />
                        {downloadingId === file.id
                          ? tr('settings.export.downloading')
                          : tr('settings.export.download')}
                      </span>
                    </Button>
                  )}
                </div>
              </div>
            ))}
          </div>
        )}

        {downloadError && (
          <div style={{ fontSize: 12, color: '#b42318', marginTop: 12 }}>{downloadError}</div>
        )}
      </Card>
    </div>
  );
}
